import { z } from 'zod';

/**
 * Rol / yetki matrisi (tek kaynak-of-truth).
 * Dokuman hali: docs/07-yetki-matrisi.md  (bu dosyadan uretilir)
 *
 * Kural: yetki kontrolu HER ZAMAN sunucu tarafinda yapilir. UI sadece gizler.
 * Ayrica her sorgu tenant_id ile filtrelenir; yetki tenant izolasyonunun yerine gecmez.
 */

export const ROLLER = [
  'OWNER', // Tesis sahibi - her sey + faturalama + tesis silme
  'MANAGER', // Otel muduru - operasyon + raporlar + fiyat
  'FRONT_DESK', // Resepsiyon - rezervasyon, check-in/out, KBS, tahsilat
  'HOUSEKEEPING', // Kat hizmetleri - sadece oda durumu
  'ACCOUNTING', // Muhasebe - folio, odeme, fatura, raporlar (kimlik verisi YOK)
  'AGENCY', // Acenta portali kullanicisi (harici)
  'API_CLIENT', // Public API anahtari (harici sistem)
  'SUPPORT', // Platform destek - kisitli, her erisim loglanir
] as const;
export const rolSchema = z.enum(ROLLER);
export type Rol = z.infer<typeof rolSchema>;

export const YETKILER = [
  // Rezervasyon & oda
  'reservation:read',
  'reservation:write',
  'reservation:cancel',
  'roomplan:read',
  'room:write',
  'housekeeping:read',
  'housekeeping:write',
  // Check-in / kimlik
  'checkin:perform',
  'identity:ocr',
  'identity:read', // maskesiz kimlik verisi gormek
  'identity:export',
  // KBS
  'kbs:submit',
  'kbs:read',
  'kbs:retry',
  // Finans
  'folio:read',
  'folio:write',
  'payment:write',
  'invoice:write',
  'rate:write',
  // Acenta
  'agency:read',
  'agency:write',
  'agency:portal', // acenta portalina giris
  'allotment:write',
  // Raporlar & yonetim
  'report:read',
  'report:financial',
  'user:manage',
  'tenant:settings',
  'tenant:billing',
  'audit:read',
  // Tesis uyum takvimi (belge/sertifika son tarihleri)
  'uyum:read',
  'uyum:write',
] as const;
export const yetkiSchema = z.enum(YETKILER);
export type Yetki = z.infer<typeof yetkiSchema>;

const HEPSI = [...YETKILER] as Yetki[];

export const ROL_YETKILERI: Record<Rol, readonly Yetki[]> = {
  OWNER: HEPSI,
  MANAGER: HEPSI.filter((y) => y !== 'tenant:billing'),
  FRONT_DESK: [
    'reservation:read',
    'reservation:write',
    'reservation:cancel',
    'roomplan:read',
    'housekeeping:read',
    'housekeeping:write',
    'checkin:perform',
    'identity:ocr',
    'identity:read',
    'kbs:submit',
    'kbs:read',
    'kbs:retry',
    'folio:read',
    'folio:write',
    'payment:write',
    'agency:read',
    'report:read',
    // Denetim geldiginde masada kim varsa belgeyi gosterebilmeli; yazma yetkisi yok.
    'uyum:read',
  ],
  HOUSEKEEPING: ['roomplan:read', 'housekeeping:read', 'housekeeping:write'],
  ACCOUNTING: [
    'reservation:read',
    'folio:read',
    'folio:write',
    'payment:write',
    'invoice:write',
    'agency:read',
    'report:read',
    'report:financial',
  ],
  AGENCY: ['agency:portal', 'reservation:read', 'reservation:write', 'reservation:cancel'],
  API_CLIENT: ['reservation:read', 'reservation:write', 'reservation:cancel'],
  SUPPORT: ['roomplan:read', 'reservation:read', 'kbs:read', 'audit:read'],
};

export function yetkiVarMi(rol: Rol, yetki: Yetki): boolean {
  return ROL_YETKILERI[rol].includes(yetki);
}

export function rollerdenYetkiler(roller: Rol[]): Set<Yetki> {
  const s = new Set<Yetki>();
  for (const r of roller) for (const y of ROL_YETKILERI[r]) s.add(y);
  return s;
}

/** Kimlik verisi gormeye yetkisi olmayan roller icin maskeleme gerekir. */
export function kimlikMaskelenmeliMi(roller: Rol[]): boolean {
  return !rollerdenYetkiler(roller).has('identity:read');
}

/**
 * Panelden acilabilecek insan hesaplari.
 *
 * Disarida birakilanlar bilerek: AGENCY bir acentaya bagli olmak zorundadir
 * (acenta ekranindan acilir), API_CLIENT bir insan girisi degildir,
 * SUPPORT ise platform tarafindan verilir.
 */
export const PANELDEN_ATANABILIR_ROLLER = [
  'OWNER',
  'MANAGER',
  'FRONT_DESK',
  'HOUSEKEEPING',
  'ACCOUNTING',
] as const satisfies readonly Rol[];

export const ROL_ADLARI: Record<Rol, string> = {
  OWNER: 'Tesis sahibi',
  MANAGER: 'Otel müdürü',
  FRONT_DESK: 'Resepsiyon',
  HOUSEKEEPING: 'Kat hizmetleri',
  ACCOUNTING: 'Muhasebe',
  AGENCY: 'Acenta',
  API_CLIENT: 'API istemcisi',
  SUPPORT: 'Destek',
};

/**
 * Yetki yukseltmesini engeller: bir kullanici, kendi yetkilerinin tamamini
 * kapsamayan bir rolu baskasina veremez.
 *
 * Ornek: MANAGER'da `tenant:billing` yoktur, bu yuzden OWNER acamaz. Aksi
 * halde mudur kendi ustunde bir hesap acip onunla giris yapabilirdi.
 */
export function rolAtanabilirMi(atayaninYetkileri: Iterable<Yetki>, hedefRol: Rol): boolean {
  const sahip = new Set(atayaninYetkileri);
  return ROL_YETKILERI[hedefRol].every((y) => sahip.has(y));
}
